import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import MailRoundedIcon from "@mui/icons-material/MailRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { useAuth } from "../../context/AuthContext";
import { useDashboardTheme } from "../../context/DashboardThemeContext";
import { useAlert } from "../../context/AlertContext";

const EnterpriseMail = () => {
  const { accessToken, logout } = useAuth();
  const { dashboardTheme } = useDashboardTheme();
  const alert = useAlert();

  const [empresa, setEmpresa] = useState({
    id: null,
    smtp_host: "",
    smtp_port: "",
    smtp_user: "",
    smtp_password: "",
    smtp_from_name: "",
    smtp_security: "SSL",
    smtp_use_custom_config: false,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const notify = useCallback(
    (message, severity = "success") => {
      if (typeof alert?.showAlert === "function") {
        alert.showAlert(message, severity);
      }
    },
    [alert]
  );

  const getToken = useCallback(() => {
    return accessToken || localStorage.getItem("accessToken") || "";
  }, [accessToken]);

  const parseJsonSafe = async (res) => {
    try {
      return await res.json();
    } catch {
      return { error: "Respuesta inválida del servidor" };
    }
  };

  const authFetch = useCallback(
    async (url, options = {}) => {
      const token = getToken();

      const headers = {
        Accept: "application/json",
        ...(options.headers || {}),
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      return fetch(url, {
        ...options,
        headers,
      });
    },
    [getToken]
  );

  const normalizeError = (data, fallback) => {
    if (!data) return fallback;
    if (typeof data.error === "string") return data.error;
    if (typeof data.detail === "string") return data.detail;
    if (typeof data.mensaje === "string") return data.mensaje;
    if (typeof data.message === "string") return data.message;

    const values = Object.values(data.error || data || {})
      .flat()
      .map((item) => (typeof item === "string" ? item : JSON.stringify(item)))
      .join(" ");

    return values || fallback;
  };

  const loadEmpresa = useCallback(async () => {
    setLoading(true);

    try {
      const res = await authFetch("/api/enterprise/empresa/obtener/");
      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (res.ok && data?.empresa) {
        const e = data.empresa;

        setEmpresa({
          id: e.id,
          smtp_host: e.smtp_host || "",
          smtp_port: e.smtp_port || "",
          smtp_user: e.smtp_user || "",
          smtp_password: e.smtp_password || "",
          smtp_from_name: e.smtp_from_name || "",
          smtp_security: e.smtp_security || "SSL",
          smtp_use_custom_config: !!e.smtp_use_custom_config,
        });
      } else {
        notify(data?.error || "No se pudo cargar la empresa.", "error");
      }
    } catch {
      notify("Error de conexión con el servidor", "error");
    } finally {
      setLoading(false);
    }
  }, [authFetch, logout, notify]);

  useEffect(() => {
    loadEmpresa();
  }, [loadEmpresa]);

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: dashboardTheme.card_background,
      color: dashboardTheme.text_color,
      "& fieldset": {
        borderColor: dashboardTheme.border_color,
      },
      "&:hover fieldset": {
        borderColor: dashboardTheme.primary_color,
      },
      "&.Mui-focused fieldset": {
        borderColor: dashboardTheme.primary_color,
      },
    },
    "& .MuiInputLabel-root": {
      color: dashboardTheme.muted_text_color,
      "&.Mui-focused": {
        color: dashboardTheme.primary_color,
      },
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: dashboardTheme.muted_text_color,
    },
    "& .MuiSvgIcon-root": {
      color: dashboardTheme.muted_text_color,
    },
  };

  const primaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    bgcolor: dashboardTheme.button_color,
    color: dashboardTheme.button_text_color,
    boxShadow: "none",
    px: 2.5,
    "&:hover": {
      bgcolor: dashboardTheme.button_hover_color || dashboardTheme.button_color,
      boxShadow: "none",
    },
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setEmpresa((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSwitch = (event) => {
    const { name, checked } = event.target;

    setEmpresa((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const submit = async (event) => {
    event.preventDefault();

    if (!empresa.id) {
      notify("Primero debe registrar la empresa.", "error");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        smtp_host: empresa.smtp_host,
        smtp_port: empresa.smtp_port ? Number(empresa.smtp_port) : null,
        smtp_user: empresa.smtp_user,
        smtp_password: empresa.smtp_password,
        smtp_from_name: empresa.smtp_from_name,
        smtp_security: empresa.smtp_security,
        smtp_use_custom_config: empresa.smtp_use_custom_config,
      };

      const res = await authFetch(`/api/enterprise/empresa/editar/${empresa.id}/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (res.ok) {
        notify(data?.mensaje || "Configuración SMTP guardada correctamente", "success");
        await loadEmpresa();
      } else {
        notify(normalizeError(data, "No se pudo guardar la configuración SMTP."), "error");
      }
    } catch {
      notify("Error de conexión con el servidor", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Stack
        spacing={1.2}
        alignItems="center"
        justifyContent="center"
        sx={{ minHeight: 260 }}
      >
        <CircularProgress size={30} sx={{ color: dashboardTheme.primary_color }} />
        <Typography sx={{ fontSize: 14, color: dashboardTheme.muted_text_color }}>
          Cargando configuración de correo...
        </Typography>
      </Stack>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: "14px",
        border: `1px solid ${dashboardTheme.border_color}`,
        bgcolor: dashboardTheme.card_background,
        boxShadow:
          dashboardTheme.theme_mode === "dark"
            ? "none"
            : "0 4px 10px rgba(0,0,0,0.08)",
      }}
    >
      <Stack spacing={0.7} sx={{ mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor:
                dashboardTheme.theme_mode === "dark"
                  ? "rgba(255,255,255,0.06)"
                  : `${dashboardTheme.primary_color}14`,
            }}
          >
            <MailRoundedIcon sx={{ color: dashboardTheme.primary_color }} />
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: { xs: 17, md: 18 },
                fontWeight: 600,
                color: dashboardTheme.text_color,
              }}
            >
              Configuración de Correo Empresarial
            </Typography>

            <Typography
              sx={{
                fontSize: 13.5,
                color: dashboardTheme.muted_text_color,
                lineHeight: 1.5,
              }}
            >
              Configura un SMTP personalizado o deja desactivada esta opción para usar el correo por defecto del sistema.
            </Typography>
          </Box>
        </Box>
      </Stack>

      {!empresa.smtp_use_custom_config && (
        <Alert
          severity="info"
          sx={{
            mb: 2.5,
            borderRadius: "10px",
            bgcolor:
              dashboardTheme.theme_mode === "dark"
                ? "rgba(6,187,199,0.12)"
                : `${dashboardTheme.primary_color}12`,
            color: dashboardTheme.text_color,
            border: `1px solid ${dashboardTheme.border_color}`,
            "& .MuiAlert-icon": {
              color: dashboardTheme.primary_color,
            },
          }}
        >
          Actualmente se usará el SMTP por defecto configurado en el servidor.
        </Alert>
      )}

      <Box component="form" onSubmit={submit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={empresa.smtp_use_custom_config}
                  onChange={handleSwitch}
                  name="smtp_use_custom_config"
                  disabled={saving}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: dashboardTheme.primary_color,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: dashboardTheme.primary_color,
                    },
                  }}
                />
              }
              label="Usar servidor SMTP personalizado"
              sx={{
                color: dashboardTheme.text_color,
                "& .MuiFormControlLabel-label": {
                  fontSize: 14,
                  fontWeight: 600,
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Servidor SMTP"
              name="smtp_host"
              fullWidth
              size="small"
              value={empresa.smtp_host}
              onChange={handleChange}
              disabled={saving || !empresa.smtp_use_custom_config}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Puerto SMTP"
              name="smtp_port"
              type="number"
              fullWidth
              size="small"
              value={empresa.smtp_port}
              onChange={handleChange}
              disabled={saving || !empresa.smtp_use_custom_config}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Correo remitente"
              name="smtp_user"
              fullWidth
              size="small"
              value={empresa.smtp_user}
              onChange={handleChange}
              disabled={saving || !empresa.smtp_use_custom_config}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Nombre del remitente"
              name="smtp_from_name"
              fullWidth
              size="small"
              value={empresa.smtp_from_name}
              onChange={handleChange}
              disabled={saving || !empresa.smtp_use_custom_config}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Contraseña SMTP"
              name="smtp_password"
              type="password"
              fullWidth
              size="small"
              value={empresa.smtp_password}
              onChange={handleChange}
              autoComplete="new-password"
              disabled={saving || !empresa.smtp_use_custom_config}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Seguridad SMTP"
              name="smtp_security"
              fullWidth
              size="small"
              value={empresa.smtp_security}
              onChange={handleChange}
              disabled={saving || !empresa.smtp_use_custom_config}
              sx={fieldSx}
            >
              <MenuItem value="SSL">SSL</MenuItem>
              <MenuItem value="TLS">TLS</MenuItem>
              <MenuItem value="NONE">Ninguno</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              type="submit"
              disabled={saving}
              startIcon={saving ? null : <SaveRoundedIcon />}
              sx={primaryButtonSx}
            >
              {saving ? "Guardando..." : "Guardar configuración"}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default EnterpriseMail;